/**
 * Theme helpers for code that cannot use CSS variables (canvas charts,
 * WebGL scenes). public/rf-theme.js owns the theme and fires 'rf-theme'.
 */
import { useEffect, useState } from 'react';

export function currentTheme() {
  if (typeof window !== 'undefined' && window.rfTheme) return window.rfTheme.get();
  return document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
}

export const isLightTheme = () => currentTheme() === 'light';

/** Re-renders when the theme changes; returns 'light' or 'dark'. */
export function useTheme() {
  const [theme, setTheme] = useState(currentTheme);
  useEffect(() => {
    const onChange = () => setTheme(currentTheme());
    window.addEventListener('rf-theme', onChange);
    return () => window.removeEventListener('rf-theme', onChange);
  }, []);
  return theme;
}

/** Axis / label ink and grid lines for canvas charts. */
export function chartColours() {
  return isLightTheme()
    ? { ink: '#3a3f4a', grid: 'rgba(16,24,40,.1)' }
    : { ink: '#e6e6e6', grid: 'rgba(255,255,255,.1)' };
}

/** Light-theme background for 3D viewers (a light grey, as CAD tools use). */
export const LIGHT_VIEWER_BG = 0xe6e8ec;

/**
 * Keep a three.js renderer's clear colour (or a scene's background) in step
 * with the theme. darkHex is the viewer's dark-theme colour. Returns an
 * unsubscribe function for the effect's clean-up.
 */
export function bindViewerBackground(target, darkHex, THREE) {
  const apply = () => {
    const hex = isLightTheme() ? LIGHT_VIEWER_BG : darkHex;
    if (target.setClearColor) target.setClearColor(hex);
    else if (THREE) target.background = new THREE.Color(hex);
  };
  apply();
  window.addEventListener('rf-theme', apply);
  return () => window.removeEventListener('rf-theme', apply);
}
